import { z } from 'zod';
import { parseGreetingsObjectString } from './greetingsUtils';

const GreetingObjectSchema = z.object({
  label: z
    .string()
    .min(1, { message: 'Label field is required and cannot be empty' })
    .trim(),
  text: z
    .string()
    .min(1, { message: 'Text field is required and cannot be empty' })
    .trim(),
});

const GreetingsArraySchema = z
  .array(GreetingObjectSchema)
  .min(1, { message: 'At least one greeting object is required' });

export const validateGreetingsObjectString = (
  input?: string
): string | false => {
  if (!input || !input.trim()) {
    return false;
  }

  const parsed = parseGreetingsObjectString(input);

  if (parsed.length === 0 && input.trim()) {
    return 'Invalid format. Expected format: {label: "labelValue", text: "textValue"}; {label: "labelValue", text: "textValue"}; Both label and text fields are required for each greeting object.';
  }

  const potentialObjectCount = (input.match(/\{[^}]*\}/g) || []).length;
  if (potentialObjectCount > parsed.length) {
    return 'Some objects appear to be malformed. Each object must follow the format: {label: "labelValue", text: "textValue"}; Ensure all objects have both label and text fields with proper quotes.';
  }

  const result = GreetingsArraySchema.safeParse(parsed);

  if (!result.success) {
    const errorMessages = result.error.errors.map(err => {
      const objectNumber = err.path.length > 0 ? Number(err.path[0]) + 1 : null;
      const fieldName = err.path.length > 1 ? err.path[1] : null;

      if (objectNumber && fieldName) {
        return `Object ${objectNumber} - ${err.message}`;
      } else if (objectNumber) {
        return `Object ${objectNumber}: ${err.message}`;
      } else {
        return err.message;
      }
    });

    const baseMessage =
      'Required format: {label: "labelValue", text: "textValue"}; Both label and text fields are required.';
    return `${errorMessages.join(', ')}. ${baseMessage}`;
  }

  return false;
};
